import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Track engagement for better install prompt timing
    let engagementScore = 0;
    let hasInteracted = false;

    const trackEngagement = () => {
      if (!hasInteracted) {
        hasInteracted = true;
        engagementScore += 1;
      }
    };

    const trackPageView = () => {
      engagementScore += 0.5;
    };

    const trackBookingInteraction = () => {
      engagementScore += 2; // Higher score for booking-related actions
    };

    // Listen for meaningful engagement
    document.addEventListener('click', trackEngagement);
    document.addEventListener('scroll', trackEngagement);
    document.addEventListener('keydown', trackEngagement);
    window.addEventListener('load', trackPageView);

    // Track booking flow interactions
    const bookingElements = document.querySelectorAll('[data-booking-action]');
    bookingElements.forEach(el => {
      el.addEventListener('click', trackBookingInteraction);
    });

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Wait for meaningful engagement before showing prompt
      const checkEngagement = () => {
        if (engagementScore >= 2 || window.location.pathname.includes('/dashboard')) {
          setShowPrompt(true);
          
          // Track install prompt shown for analytics
          if (window.gtag) {
            window.gtag('event', 'pwa_install_prompt_shown', {
              event_category: 'PWA',
              event_label: 'Install Prompt',
              engagement_score: engagementScore
            });
          }
        } else {
          // Check again in 30 seconds
          setTimeout(checkEngagement, 30000);
        }
      };

      // Start checking after initial delay
      setTimeout(checkEngagement, 15000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if prompt was dismissed before
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        // Don't show for a week after dismissal
        setShowPrompt(false);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      document.removeEventListener('click', trackEngagement);
      document.removeEventListener('scroll', trackEngagement);
      document.removeEventListener('keydown', trackEngagement);
      window.removeEventListener('load', trackPageView);
      bookingElements.forEach(el => {
        el.removeEventListener('click', trackBookingInteraction);
      });
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Track install prompt interaction
    if (window.gtag) {
      window.gtag('event', 'pwa_install_prompt_clicked', {
        event_category: 'PWA',
        event_label: 'Install Button Clicked'
      });
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    // Track the outcome
    if (window.gtag) {
      window.gtag('event', 'pwa_install_outcome', {
        event_category: 'PWA',
        event_label: outcome,
        value: outcome === 'accepted' ? 1 : 0
      });
    }
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      // Store successful install
      localStorage.setItem('pwa-installed', Date.now().toString());
    } else {
      console.log('User dismissed the install prompt');
      // Store dismissal with longer delay
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    // Track dismissal
    if (window.gtag) {
      window.gtag('event', 'pwa_install_prompt_dismissed', {
        event_category: 'PWA',
        event_label: 'Prompt Dismissed'
      });
    }
    
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed or not supported
  if (isStandalone || (!deferredPrompt && !isIOS) || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto pwa-install-prompt">
      <div 
        className="bg-white border border-figgy-200 rounded-lg shadow-lg p-4"
        role="dialog"
        aria-labelledby="pwa-install-title"
        aria-describedby="pwa-install-description"
        aria-live="polite"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0" aria-hidden="true">
              <Smartphone className="h-6 w-6 text-figgy-600" />
            </div>
            <div className="flex-1">
              <h3 id="pwa-install-title" className="text-sm font-semibold text-gray-900">
                Install Figgy App
              </h3>
              <p id="pwa-install-description" className="text-xs text-gray-600 mt-1">
                {isIOS 
                  ? "Tap Share → Add to Home Screen for quick access"
                  : "Get the app for faster access and offline use"
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600"
            aria-label="Dismiss install prompt"
            type="button"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        
        {!isIOS && (
          <div className="mt-3">
            <Button
              onClick={handleInstallClick}
              size="sm"
              className="w-full bg-figgy-600 hover:bg-figgy-700 text-white"
              aria-describedby="pwa-install-description"
              type="button"
            >
              <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              Install App
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
